/**
 * SUSI — <media-gallery>
 *
 * One list of product media with two faces:
 *   carousel  (always below 768px, and on larger screens for the "carousel-*" layouts)
 *             horizontal scroll-snap strip with a neighbour peeking in on phones, a progress bar,
 *             arrows + thumbnails on larger screens
 *   stack     (larger screens, "columns-*" / "collage-*" layouts) every item visible in a grid
 *
 * Attributes: data-layout, data-context, data-zoom (lightbox on), data-preload, data-grouping
 * Children:   [data-gallery-viewport] > [data-gallery-list] > [data-gallery-item][data-media-id]
 *             [data-gallery-thumbs] li[data-media-id] > [data-gallery-thumb]
 *             [data-gallery-prev] / [data-gallery-next], [data-gallery-progress]
 *             [data-gallery-zoom] (link to the large image), template[data-gallery-viewer-template]
 *             script[data-gallery-groups] (media ↔ option value map for variant filtering)
 * API:        setActiveMedia(mediaId, { scroll, instant, user }), activeMediaId, next(), prev()
 * Events:     on:media-gallery:change { mediaId } (bubbles) when the active item changes
 * Listens:    on:variant:change on the product root
 */
(() => {
  if (customElements.get('media-gallery')) return;

  const theme = window.theme;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.list = this.querySelector('[data-gallery-list]');
      if (!this.viewport || !this.list) return;

      this.items = Array.from(this.list.querySelectorAll('[data-gallery-item]'));
      this.thumbsBox = this.querySelector('[data-gallery-thumbs]');
      this.thumbsScroller = this.thumbsBox ? this.thumbsBox.querySelector('[data-gallery-thumbs-scroll]') || this.thumbsBox : null;
      this.thumbItems = this.thumbsBox ? Array.from(this.thumbsBox.querySelectorAll('li[data-media-id]')) : [];
      this.progress = this.querySelector('[data-gallery-progress]');
      this.prevButton = this.querySelector('[data-gallery-prev]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.xrButton = this.querySelector('[data-shopify-xr]');
      this.root = this.closest('[data-product-root]') || this.parentElement;
      this.wide = window.matchMedia('(min-width: 768px)');
      this.activeItem = this.items.find((item) => item.classList.contains('is-active') && !item.hidden) || this.visibleItems[0] || null;

      this.groupData = this.readGroups();
      this.currentGroup = undefined;

      this.onClick = this.onClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onLayoutChange = this.onLayoutChange.bind(this);
      this.onViewerSelect = this.onViewerSelect.bind(this);
      this.onPointerIntent = this.onPointerIntent.bind(this);

      this.addEventListener('click', this.onClick);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.root.addEventListener('on:variant:change', this.onVariantChange);
      this.wide.addEventListener('change', this.onLayoutChange);
      window.addEventListener('on:debounced-resize', this.onLayoutChange);

      if (this.hasAttribute('data-preload')) {
        this.viewport.addEventListener('pointerenter', this.onPointerIntent);
        this.viewport.addEventListener('touchstart', this.onPointerIntent, { passive: true });
        this.preloadTimer = setTimeout(() => this.preloadImages(), 3000);
      }

      this.prepareVideos();

      if (this.groupData) {
        // Start from what the server already filtered, then confirm with the picker once it runs
        this.applyGroup(this.groupFromMarkup());
        customElements.whenDefined('variant-picker').then(() => {
          const picker = this.isConnected ? this.root.querySelector('variant-picker') : null;
          if (!picker || !picker.selectedOptions) return;
          if (this.applyGroup(picker.selectedOptions[this.groupData.index] || null)) {
            this.setActive(this.visibleItems.find((item) => item === this.activeItem) || this.visibleItems[0], { scroll: this.isCarousel, instant: true, silent: true });
          }
        });
      }

      this.observer = new ResizeObserver(() => this.scheduleHeight());
      this.items.forEach((item) => this.observer.observe(item));

      this.setAttribute('data-ready', '');
      this.setActive(this.activeItem, { scroll: this.isCarousel, instant: true, silent: true });
    }

    disconnectedCallback() {
      if (!this.viewport) return;
      this.removeEventListener('click', this.onClick);
      this.viewport.removeEventListener('scroll', this.onScroll);
      this.viewport.removeEventListener('pointerenter', this.onPointerIntent);
      this.viewport.removeEventListener('touchstart', this.onPointerIntent);
      if (this.root) this.root.removeEventListener('on:variant:change', this.onVariantChange);
      this.wide.removeEventListener('change', this.onLayoutChange);
      window.removeEventListener('on:debounced-resize', this.onLayoutChange);
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.heightTimer);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      clearTimeout(this.preloadTimer);
      clearTimeout(this.highlightTimer);
      if (this.viewer) {
        this.viewer.removeEventListener('on:gallery-viewer:select', this.onViewerSelect);
        this.viewer.remove();
        this.viewer = null;
      }
    }

    /**
     * Stacked layouts play autoplaying videos as silent background loops. On a phone the same
     * list is a carousel, where a video should behave like a normal player with controls, so the
     * not-yet-loaded players are switched over before they start.
     */
    prepareVideos() {
      if (this.isCarousel === false) return;
      this.querySelectorAll('deferred-media:not([loaded]) > template').forEach((template) => {
        const player = template.content.querySelector('video-component[data-background="true"]');
        if (!player) return;
        player.dataset.background = 'false';
        const video = player.querySelector('video');
        if (video) video.controls = true;
      });
    }

    /* -- State -------------------------------------------------------------- */

    get layout() {
      return this.dataset.layout || 'carousel-under';
    }

    /** True when the list is a horizontal strip right now. */
    get isCarousel() {
      return !this.wide.matches || this.layout.startsWith('carousel');
    }

    /** Shown items in DOM order (filtering by variant re-orders the list). */
    get visibleItems() {
      return Array.from(this.list.querySelectorAll('[data-gallery-item]:not([hidden])'));
    }

    get activeMediaId() {
      return this.activeItem ? this.activeItem.dataset.mediaId : null;
    }

    itemFor(mediaId) {
      const id = String(mediaId);
      return this.items.find((item) => item.dataset.mediaId === id) || null;
    }

    /* -- Navigation --------------------------------------------------------- */

    next() {
      this.step(1);
    }

    prev() {
      this.step(-1);
    }

    step(direction) {
      const visible = this.visibleItems;
      const index = visible.indexOf(this.activeItem);
      const target = visible[index + direction];
      if (target) this.setActive(target, { scroll: true, user: true });
    }

    /**
     * Makes a media item the active one.
     * @param {string|number} mediaId
     * @param {object} [options] - { scroll: boolean, instant: boolean, user: boolean }
     */
    setActiveMedia(mediaId, options = {}) {
      const item = this.itemFor(mediaId);
      if (item && !item.hidden) this.setActive(item, Object.assign({ scroll: true }, options));
    }

    setActive(item, options = {}) {
      if (!item) return;
      const { scroll = false, instant = false, user = false, silent = false } = options;
      const changed = item !== this.activeItem;
      this.activeItem = item;

      this.items.forEach((other) => other.classList.toggle('is-active', other === item));
      this.thumbItems.forEach((thumbItem) => {
        const current = thumbItem.dataset.mediaId === item.dataset.mediaId;
        const button = thumbItem.querySelector('[data-gallery-thumb]');
        if (!button) return;
        button.classList.toggle('is-active', current);
        if (current) {
          button.setAttribute('aria-current', 'true');
          this.revealThumb(thumbItem, instant);
        } else {
          button.removeAttribute('aria-current');
        }
      });

      if (scroll) this.reveal(item, instant);
      this.updateControls();
      this.scheduleHeight();

      if (this.xrButton && item.dataset.mediaType === 'model') {
        this.xrButton.setAttribute('data-shopify-model3d-id', item.dataset.mediaId);
      }

      if (!changed || silent) return;

      if (this.isCarousel) {
        theme.pauseAllMedia(this);
        this.playMedia(item, user);
      }
      theme.emit('on:media-gallery:change', { mediaId: item.dataset.mediaId }, this);
    }

    /** Starts the video of an item: always when the visitor picked it, otherwise only if it autoplays. */
    playMedia(item, user) {
      const deferred = item.querySelector('deferred-media');
      if (deferred) {
        const autoplay = deferred.dataset.autoplay === 'true';
        if ((user || autoplay) && typeof deferred.loadContent === 'function' && !deferred.hasAttribute('loaded')) {
          deferred.loadContent(false);
          return;
        }
        const video = deferred.querySelector('video');
        if (video && (user || autoplay)) {
          const attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
        }
      }
    }

    reveal(item, instant) {
      const behavior = instant || prefersReducedMotion() ? 'auto' : 'smooth';

      if (this.isCarousel) {
        const box = this.viewport.getBoundingClientRect();
        const rect = item.getBoundingClientRect();
        const delta = rect.left + rect.width / 2 - (box.left + box.width / 2);
        if (Math.abs(delta) < 1) return;
        this.ignoreScroll = true;
        // scrollTo, not scrollBy: a relative scroll halts at the next slide because of scroll-snap-stop
        this.viewport.scrollTo({ left: this.viewport.scrollLeft + delta, behavior });
        this.scheduleHeight();
        return;
      }

      if (instant) return;
      const rect = item.getBoundingClientRect();
      const top = theme.stickyHeaderHeight();
      if (rect.top < top || rect.top > window.innerHeight * 0.6) theme.scrollToElement(item);

      // Briefly dim the other items so the eye lands on the right one
      clearTimeout(this.highlightTimer);
      this.items.forEach((other) => other.classList.toggle('is-dimmed', other !== item));
      this.highlightTimer = setTimeout(() => {
        this.items.forEach((other) => other.classList.remove('is-dimmed'));
      }, 1200);
    }

    revealThumb(thumbItem, instant) {
      if (!this.thumbsScroller || !this.thumbsScroller.offsetParent) return;
      const box = this.thumbsScroller.getBoundingClientRect();
      const rect = thumbItem.getBoundingClientRect();
      let left = 0;
      let top = 0;
      if (rect.left < box.left) left = rect.left - box.left - 8;
      else if (rect.right > box.right) left = rect.right - box.right + 8;
      if (rect.top < box.top) top = rect.top - box.top - 8;
      else if (rect.bottom > box.bottom) top = rect.bottom - box.bottom + 8;
      if (left || top) this.thumbsScroller.scrollBy({ left, top, behavior: instant || prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    updateControls() {
      const visible = this.visibleItems;
      const index = Math.max(0, visible.indexOf(this.activeItem));
      this.dataset.visible = String(visible.length);
      this.style.setProperty('--gallery-count', String(Math.max(1, visible.length)));
      this.style.setProperty('--gallery-index', String(index));
      if (this.prevButton) this.prevButton.disabled = index <= 0;
      if (this.nextButton) this.nextButton.disabled = index >= visible.length - 1;
      if (this.progress) this.progress.hidden = visible.length < 2;
    }

    /**
     * Height changes wait until the strip has stopped moving: a layout change in the middle of a
     * swipe or a smooth scroll makes the browser snap back to the slide it started from.
     */
    scheduleHeight() {
      clearTimeout(this.heightTimer);
      this.heightTimer = setTimeout(() => {
        // The strip is at rest: scripted scrolling is over, the visitor's scrolling counts again
        this.ignoreScroll = false;
        this.updateHeight();
      }, 140);
    }

    /** In carousel mode the strip takes the height of the active item when items differ. */
    updateHeight() {
      if (!this.isCarousel || !this.activeItem) {
        this.viewport.style.height = '';
        return;
      }
      const heights = this.visibleItems.map((item) => item.offsetHeight).filter(Boolean);
      const uneven = heights.length > 1 && Math.max(...heights) - Math.min(...heights) > 2;
      this.viewport.style.height = uneven && this.activeItem.offsetHeight ? `${this.activeItem.offsetHeight}px` : '';
    }

    /* -- Events ------------------------------------------------------------- */

    onClick(event) {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb && this.contains(thumb)) {
        const holder = thumb.closest('[data-media-id]');
        if (holder) this.setActiveMedia(holder.dataset.mediaId, { user: true });
        return;
      }

      if (event.target.closest('[data-gallery-prev]')) {
        this.prev();
        return;
      }
      if (event.target.closest('[data-gallery-next]')) {
        this.next();
        return;
      }

      const zoom = event.target.closest('[data-gallery-zoom]');
      if (zoom && this.hasAttribute('data-zoom') && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
        event.preventDefault();
        this.openViewer(zoom);
      }
    }

    onScroll() {
      if (!this.isCarousel) return;
      this.scheduleHeight();
      if (this.ignoreScroll) return;
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const box = this.viewport.getBoundingClientRect();
        const middle = box.left + box.width / 2;
        let nearest = null;
        let distance = Infinity;
        this.visibleItems.forEach((item) => {
          const rect = item.getBoundingClientRect();
          const gap = Math.abs(rect.left + rect.width / 2 - middle);
          if (gap < distance) {
            distance = gap;
            nearest = item;
          }
        });
        if (nearest && nearest !== this.activeItem) this.setActive(nearest, { scroll: false });
      });
    }

    onLayoutChange() {
      this.updateHeight();
      if (this.isCarousel && this.activeItem) this.reveal(this.activeItem, true);
    }

    onVariantChange(event) {
      const { variant, selectedOptions } = event.detail || {};
      let groupChanged = false;
      if (this.groupData && Array.isArray(selectedOptions)) {
        groupChanged = this.applyGroup(selectedOptions[this.groupData.index] || null);
      }

      const media = variant && variant.featured_media ? this.itemFor(variant.featured_media.id) : null;
      if (media && !media.hidden) {
        this.setActive(media, { scroll: true, instant: groupChanged });
      } else if (groupChanged) {
        this.setActive(this.visibleItems[0], { scroll: true, instant: true });
      }
    }

    onViewerSelect(event) {
      this.setActiveMedia(event.detail.mediaId, { instant: true });
    }

    /* -- Filtering by variant ----------------------------------------------- */

    readGroups() {
      if (!this.hasAttribute('data-grouping')) return null;
      const source = this.querySelector('[data-gallery-groups]');
      if (!source) return null;
      try {
        return JSON.parse(source.textContent);
      } catch (error) {
        return null;
      }
    }

    /** The group the server pre-filtered for (before the variant picker script is ready). */
    groupFromMarkup() {
      if (!this.items.some((item) => item.hidden)) return null;
      const shown = this.items.find((item) => {
        const info = this.groupData.media[item.dataset.mediaId];
        return !item.hidden && info && !info.all && info.group;
      });
      return shown ? this.groupData.media[shown.dataset.mediaId].group : null;
    }

    /**
     * Shows only the media of one option value (plus the "all-groups" media).
     * @param {?string} group - Option value name, or null for everything.
     * @returns {boolean} Whether the visible set changed.
     */
    applyGroup(group) {
      if (!this.groupData || group === this.currentGroup) return false;
      this.currentGroup = group;

      const lists = [this.items, this.thumbItems];
      if (this.viewer) lists.push(Array.from(this.viewer.querySelectorAll('[data-viewer-thumb]')));

      lists.forEach((elements) => {
        if (!elements.length) return;
        const parent = elements[0].parentElement;
        const leading = [];
        const regular = [];
        const trailing = [];
        const hidden = [];

        elements.forEach((element) => {
          const info = this.groupData.media[element.dataset.mediaId] || {};
          const show = !group || info.all || info.group === group;
          element.hidden = !show;
          if (!show) hidden.push(element);
          else if (info.all && info.position === 'start') leading.push(element);
          else if (info.all) trailing.push(element);
          else regular.push(element);
        });

        // Shared media goes to the edges, hidden items to the end so :nth-child layouts stay tidy
        leading.concat(regular, trailing, hidden).forEach((element) => parent.appendChild(element));
      });

      this.updateControls();
      return true;
    }

    /* -- Zoom viewer -------------------------------------------------------- */

    openViewer(link) {
      const item = link.closest('[data-gallery-item]');
      if (!this.viewer) {
        const template = this.querySelector('[data-gallery-viewer-template]');
        if (!template) return;
        this.viewer = template.content.firstElementChild.cloneNode(true);
        this.viewer.addEventListener('on:gallery-viewer:select', this.onViewerSelect);
        document.body.appendChild(this.viewer);
        if (this.groupData) {
          const group = this.currentGroup;
          this.currentGroup = undefined;
          this.applyGroup(group);
        }
      }
      customElements.whenDefined('gallery-viewer').then(() => {
        this.viewer.open(item ? item.dataset.mediaId : null, link);
      });
    }

    /* -- Preloading --------------------------------------------------------- */

    /** Warm up the zoom image as soon as the visitor shows interest in the gallery. */
    onPointerIntent() {
      if (!this.hasAttribute('data-zoom') || !this.activeItem) return;
      const link = this.activeItem.querySelector('[data-gallery-zoom]');
      if (!link || link.dataset.preloaded) return;
      link.dataset.preloaded = 'true';
      const image = new Image();
      image.src = link.href;
    }

    /** Stops lazy loading for the gallery pictures so swiping never shows an empty slide. */
    preloadImages() {
      if (!this.offsetParent) return;
      this.list.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
      });
    }
  }

  customElements.define('media-gallery', MediaGallery);
})();
